import { doc, getDoc, updateDoc, collection, query, where, orderBy, getDocs, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// ═══════════════ TRIP STATUS CONSTANTS ═══════════════
export const TRIP_STATUSES = {
  PENDING_POOL: 'pending_pool',
  PENDING_TARGETED: 'pending_targeted',
  PENDING: 'pending', // legacy alias for pre-pool bookings
  CONFIRMED: 'confirmed',
  EN_ROUTE: 'en_route',
  ARRIVED: 'arrived',
  PIN_VERIFIED: 'pin_verified',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const ACTIVE_STATUSES = ['pending_pool', 'pending_targeted', 'pending', 'confirmed', 'en_route', 'arrived', 'pin_verified', 'in_progress'];
export const PRE_DRIVER_STATUSES = ['pending_pool', 'pending_targeted', 'pending'];

// ═══════════════ LISTEN TO BOOKING UPDATES ═══════════════
export function subscribeToBooking(bookingId, callback) {
  return onSnapshot(doc(db, 'bookings', bookingId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

// ═══════════════ DRIVER: START EN ROUTE ═══════════════
export async function startEnRoute(bookingId) {
  const fn = httpsCallable(functions, 'updateTripStatus');
  return (await fn({ bookingId, status: 'en_route' })).data;
}

// ═══════════════ DRIVER: REPORT ARRIVAL (manual fallback) ═══════════════
export async function reportArrival(bookingId, location) {
  const fn = httpsCallable(functions, 'reportDriverArrival');
  return (await fn({ bookingId, lat: location.lat, lng: location.lng })).data;
}

// ═══════════════ DRIVER: COMPLETE TRIP ═══════════════
export async function completeTrip(bookingId, finalLocation, deviationReason) {
  const fn = httpsCallable(functions, 'completeTrip');
  return (await fn({ bookingId, lat: finalLocation.lat, lng: finalLocation.lng, deviationReason: deviationReason || null })).data;
}

// ═══════════════ UPDATE DRIVER LOCATION ═══════════════
export async function updateDriverLocation(bookingId, lat, lng) {
  await updateDoc(doc(db, 'bookings', bookingId), {
    'driverLocation.lat': lat,
    'driverLocation.lng': lng,
    'driverLocation.updatedAt': serverTimestamp(),
  });
}

// ═══════════════ RECORD TRIP DISTANCE (driver, during in_progress) ═══════════════
export async function recordTripDistance(bookingId, lat, lng) {
  const fn = httpsCallable(functions, 'recordTripDistance');
  return (await fn({ bookingId, lat, lng })).data;
}

// ═══════════════ GET TRIP HISTORY ═══════════════
export async function getTripHistory(userId, role, maxResults = 50) {
  const field = role === 'rider' ? 'riderId' : 'driverId';
  const q = query(
    collection(db, 'bookings'),
    where(field, '==', userId),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.slice(0, maxResults).map((d) => ({ id: d.id, ...d.data() }));
}

// ═══════════════ GET ACTIVE TRIPS ═══════════════
export async function getActiveTrips(userId, role) {
  const field = role === 'rider' ? 'riderId' : 'driverId';
  const q = query(
    collection(db, 'bookings'),
    where(field, '==', userId),
    where('status', 'in', ['confirmed', 'en_route', 'arrived', 'pin_verified', 'in_progress'])
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ═══════════════ SUBSCRIBE TO ACTIVE TRIPS ═══════════════
export function subscribeToActiveTrips(userId, role, callback) {
  const field = role === 'rider' ? 'riderId' : 'driverId';
  const q = query(
    collection(db, 'bookings'),
    where(field, '==', userId),
    where('status', 'in', ['confirmed', 'en_route', 'arrived', 'pin_verified', 'in_progress'])
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════ SUBSCRIBE TO POOL BOOKINGS (driver-side) ═══════════════
// Live listener returning all pending_pool bookings the driver could see (tier-filtered).
// Eligibility (capacity, WWCC, cool-off, time conflict, visibility delay) is computed
// client-side for live filtering, since cloud function call wouldn't auto-update.
export function subscribeToPoolBookings(eligibleTiers, callback) {
  if (!eligibleTiers || eligibleTiers.length === 0) { callback([]); return () => {}; }
  const q = query(
    collection(db, 'bookings'),
    where('status', '==', 'pending_pool'),
    where('tier', 'in', eligibleTiers),
    orderBy('scheduledPickupTime', 'asc')
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════ SUBSCRIBE TO TARGETED OFFERS FOR DRIVER ═══════════════
// Live listener returning bookings currently offered exclusively to this driver.
export function subscribeToTargetedOffersForDriver(driverId, callback) {
  const q = query(
    collection(db, 'bookings'),
    where('status', '==', 'pending_targeted'),
    where('driverId', '==', driverId)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════ WAITING TIMER OPERATIONS ═══════════════
export async function getWaitingTimer(bookingId) {
  const snap = await getDoc(doc(db, 'bookings', bookingId));
  return snap.exists() ? snap.data().waitingTimer || null : null;
}

export async function updateWaitingTimer(bookingId, timerData) {
  await updateDoc(doc(db, 'bookings', bookingId), {
    waitingTimer: timerData,
    updatedAt: serverTimestamp(),
  });
}

// ═══════════════ GEOFENCE LOG ═══════════════
export async function logGeofenceEvent(bookingId, event) {
  const fn = httpsCallable(functions, 'logGeofenceEvent');
  return (await fn({ bookingId, ...event })).data;
}

// ═══════════════ DRIVER CHECK-IN ═══════════════
export async function driverCheckIn(bookingId) {
  const fn = httpsCallable(functions, 'driverCheckIn');
  return (await fn({ bookingId })).data;
}

// ═══════════════ MULTI-STOP: MARK STOP COMPLETE (driver) ═══════════════
export async function markStopComplete(bookingId, stopIndex, location) {
  const fn = httpsCallable(functions, 'markStopComplete');
  return (await fn({ bookingId, stopIndex, location })).data;
}

// ═══════════════ MULTI-STOP: MARK STOP UNREACHABLE (driver) ═══════════════
export async function markStopUnreachable(bookingId, stopIndex, reason) {
  const fn = httpsCallable(functions, 'markStopUnreachable');
  return (await fn({ bookingId, stopIndex, reason })).data;
}

// ═══════════════ MULTI-STOP: ACCEPT STOP CHANGE (driver) ═══════════════
export async function acceptStopChange(bookingId, changeId) {
  const fn = httpsCallable(functions, 'acceptStopChange');
  return (await fn({ bookingId, changeId })).data;
}

// ═══════════════ MULTI-STOP: DECLINE STOP CHANGE (driver) ═══════════════
export async function declineStopChange(bookingId, changeId, reason) {
  const fn = httpsCallable(functions, 'declineStopChange');
  return (await fn({ bookingId, changeId, reason })).data;
}

// ═══════════════ MULTI-STOP: RECALCULATE FARE PREVIEW (rider) ═══════════════
// Used by rider's UI before committing a stop change — returns fare delta.
export async function recalculateFareForStops(bookingId, proposedStops) {
  const fn = httpsCallable(functions, 'recalculateFareForStops');
  return (await fn({ bookingId, proposedStops })).data;
}

// ═══════════════ MULTI-STOP: PROPOSE STOP CHANGE (rider, mid-trip) ═══════════════
// changeType: 'add' | 'remove' | 'reorder'
export async function proposeStopChange(bookingId, changeType, proposedStops) {
  const fn = httpsCallable(functions, 'proposeStopChange');
  return (await fn({ bookingId, changeType, proposedStops })).data;
}